import { AdminAudienceViewDto } from "@swagger/typescript-fetch-goflyer";
import { ReviewChangeRequest } from "../../types";

export type CrSubmitterKind = "admin" | "audience" | "anonymous" | "unknown";

export interface CrSubmitter {
  kind: CrSubmitterKind;
  name: string;
  initials: string;
  audienceKey?: string;
}

/** i18n-resolved labels injected by the hook so this module stays pure. */
export interface CrSubmitterLabels {
  admin: string;
  anonymous: string;
  unknown: string;
}

/** Up to two uppercase initials from a display name; "?" when empty. */
export const initialsFromName = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
};

/**
 * Resolve who submitted a change request: admin, a named audience, an
 * anonymous reviewer, or unknown. Pure — i18n labels are passed in.
 */
export const resolveCrSubmitter = (
  cr: ReviewChangeRequest,
  audiences: AdminAudienceViewDto[] | undefined,
  labels: CrSubmitterLabels,
): CrSubmitter => {
  if (cr.createdByAdminId) {
    return { kind: "admin", name: labels.admin, initials: "AD" };
  }
  if (cr.audienceId) {
    const match = (audiences ?? []).find((a) => a.id === cr.audienceId);
    if (match?.displayName) {
      return {
        kind: "audience",
        name: match.displayName,
        initials: initialsFromName(match.displayName),
        audienceKey: match.audienceKey,
      };
    }
    const fallback = cr.submittedBy || cr.submittedByAudienceKey;
    if (fallback) {
      return {
        kind: "audience",
        name: fallback,
        initials: initialsFromName(fallback),
        audienceKey: cr.submittedByAudienceKey,
      };
    }
    return { kind: "unknown", name: labels.unknown, initials: "?" };
  }

  const hint = cr.submittedBy || cr.submittedByAudienceKey;
  return {
    kind: "anonymous",
    name: hint ? `${labels.anonymous} (${hint})` : labels.anonymous,
    initials: "?",
  };
};
